import { useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../../App";
import PopUp from "../PopUp";
import api from "../api/api";
import functions from "../../functions/functions";
import LoadIcon from "../../icons/LoadIcon";
import { useAppStore } from "../../store/useAppStore";
import { paymentErrorHint, paymentErrorSummary } from "../../functions/paymentErrors";
import DojoPaymentPopup, { DOJO_PAYMENT_METHOD_ID } from "./DojoPaymentPopup";

// Oltre l'attesa massima dell'agente di stampa (~305 s sul ramo PAX): se non arriva nessun esito il popup
// non resta in attesa all'infinito, ma lo stato dell'ordine non viene toccato (il terminale potrebbe aver incassato).
const PAYMENT_WAIT_TIMEOUT_MS = 320000;

function TerminalPaymentPopup({ order, edit }) {
	const { setPosPaymentPopup, setLoading, setConfirm, init_cart, config, loadNextSplittedOrder } =
		useContext(AppContext);
	const { env } = useAppStore();
	const [error, setError] = useState(false);
	const [errorInfo, setErrorInfo] = useState({ code: null, message: null });
	const [timedOut, setTimedOut] = useState(false);
	const waitTimeout = useRef(null);
	// Se il backend non ha ancora le colonne payment_error_* la richiesta fallisce: si ripiega su id e status.
	const withErrorFields = useRef(true);

	useEffect(() => {
		if (order.payments.find((p) => p.payment_method_id == 20)) {
			if (env != "localhost") init_eftpos_transaction();
		}

		if (env == "localhost" && config.virtual_printer_id) {
			window.setTimeout(async () => {
				const res = await api.postProtected("/orders/edit/" + order.id + "/", {
					status: "confirmed",
					bill: order.invoice == 1 ? 4 : 1,
				});
				if (res.success != 1) {
					setConfirm({
						title: "Errore",
						message: res.error,
					});
				}
			}, 5000);
		}

		checkOrder();
		armWaitTimeout();
		const versionTimeout = window.setInterval(() => checkOrder(), 5000);
		return () => {
			clearInterval(versionTimeout);
			clearTimeout(waitTimeout.current);
		};
	}, []);

	const armWaitTimeout = () => {
		clearTimeout(waitTimeout.current);
		waitTimeout.current = window.setTimeout(() => setTimedOut(true), PAYMENT_WAIT_TIMEOUT_MS);
	};

	const checkOrder = async () => {
		console.log("checkOrder");
		let res = await api.postProtected("/orders/get/" + order.id + "/", {
			depth: 0,
			fields: withErrorFields.current
				? ["id", "status", "payment_error_code", "payment_error_message"]
				: ["id", "status"],
		});
		if (res.success != 1 && withErrorFields.current) {
			withErrorFields.current = false;
			res = await api.postProtected("/orders/get/" + order.id + "/", {
				depth: 0,
				fields: ["id", "status"],
			});
		}
		if (res.success == 1) {
			if (res.data.status == "confirmed" && (res.data.bill == 3 || res.data.bill == 4)) {
				init_cart();
				loadNextSplittedOrder();
				setPosPaymentPopup(false);
				window.parent.postMessage("close", "*");
			}
			if (res.data.status == "payment_error") {
				setError(res.data.status);
				setErrorInfo({
					code: res.data.payment_error_code || null,
					message: res.data.payment_error_message || null,
				});
			}
		} else
			setConfirm({
				title: "Errore",
				message: res.error,
			});
	};

	const cancel = async () => {
		setConfirm({
			title: "Attenzione",
			message: "Sei sicuro di annullare il pagamento?",
			onConfirm: async () => {
				if (!edit) {
					setLoading(true);
					const res = await api.postProtected("/orders/edit/" + order.id + "/", {
						status: "archived",
					});
					setLoading(false);
					if (res.success == 1) {
						setPosPaymentPopup(false);
					} else
						setConfirm({
							title: "Errore",
							message: res.error,
						});
				} else setPosPaymentPopup(false);
			},
		});
	};

	const retry = async () => {
		setLoading(true);
		const res = await api.postProtected("/orders/edit/" + order.id + "/", {
			status: "awaiting_payment",
		});
		if (order.payments.find((p) => p.payment_method_id == 20)) {
			await init_eftpos_transaction();
		}
		setLoading(false);
		if (res.success == 1) {
			setError(false);
			setTimedOut(false);
			armWaitTimeout();
			setErrorInfo({ code: null, message: null });
		} else
			setConfirm({
				title: "Errore",
				message: res.error,
			});
	};

	const getAmount = () => {
		if (order.payments.length > 0) {
			let total = 0;
			order.payments.forEach((payment) => {
				if (payment.payment_method_id == 14) {
					total += payment.amount;
				}
				if (payment.payment_method_id == 20) {
					total += payment.amount;
				}
				if (payment.payment_method_id == 22) {
					total += payment.amount;
				}
			});
			return total;
		} else {
			return order.total;
		}
	};

	const init_eftpos_transaction = async () => {
		const res = await api.postProtected("/vivawallet/init_eftpos_transaction/", {
			shop_id: order.shop_id,
			total: order.total,
			description: "Pagamento ordine " + order.id,
			order_id: order.id,
			terminal_id: config.viva_terminal_id,
		});
		if (res.success != 1) {
			setConfirm({
				title: "Errore",
				message: res.error,
				onCancel: () => setPosPaymentPopup(false),
			});
		}
	};

	// errore dichiarato dall'agente oppure nessun esito entro la scadenza
	const showError = error || timedOut;

	// Chiusura dopo l'errore: l'ordine resta in Vendite (Riprova / Annulla), il carrello si svuota per non
	// ricreare un secondo ordine con gli stessi prodotti (YELLGO-6314). Niente loadNextSplittedOrder: la parte
	// in errore non e' pagata e non va persa di vista passando alla successiva.
	const closeAfterError = () => {
		init_cart();
		setPosPaymentPopup(false);
	};

	return (
		<>
			<PopUp
				customClass={showError ? "electronic-payment electronic-payment-error" : "electronic-payment"}
				size=""
				options={{
					onClose: showError ? closeAfterError : false,
					title: "Pagamento Elettronico",
				}}
			>
				{!showError ? (
					<div className="inner-content">
						<span>
							<LoadIcon></LoadIcon>
						</span>
						<h3>Richiesta di pagamento in corso</h3>
						<div className="total-import">
							<h3>Importo totale:</h3>{" "}
							<h1>{functions.formatter.format(getAmount())}</h1>
						</div>
						<h5>
							Verifica il dispositivo di pagamento per procedere. <br></br>
							<strong>
								<u>ATTENZIONE</u> per annullare la transazione procedere sul
								terminale.
							</strong>{" "}
						</h5>
					</div>
				) : (
					<div className="inner-content error-retry">
						<h3>Errore</h3>
						<h1>Errore pagamento</h1>
						<h3 className="payment-error-code">
							{errorInfo.code
								? paymentErrorSummary({
										payment_error_code: errorInfo.code,
										payment_error_message: errorInfo.message,
								  })
								: timedOut
								? "Il terminale di pagamento non ha risposto nei tempi previsti"
								: "Pagamento non riuscito (causa non disponibile)"}
						</h3>
						{paymentErrorHint(errorInfo.code) && (
							<h5 className="payment-error-hint">{paymentErrorHint(errorInfo.code)}</h5>
						)}
						{errorInfo.code && <h5 className="payment-error-technical-code">Codice: {errorInfo.code}</h5>}
						<button onClick={retry}>Riprova</button>
						<button className="payment-error-cancel" onClick={cancel}>
							Annulla pagamento
						</button>
					</div>
				)}
			</PopUp>
		</>
	);
}

// Il pagamento Dojo (23) ha un flusso dedicato: sessione sul terminale avviata dall'API e stato letto ogni secondo
function PosPaymentPopup({ order, edit }) {
	if (order?.payments?.find((p) => p.payment_method_id == DOJO_PAYMENT_METHOD_ID)) {
		return <DojoPaymentPopup order={order} edit={edit} />;
	}
	return <TerminalPaymentPopup order={order} edit={edit} />;
}

export default PosPaymentPopup;
